/**
 * Échantillons offerts dans le sidecart — UI en accordéon inline (iso maquette Figma).
 *
 * Principe (inspiré du thème/checkout Kusmi) :
 *  - Les échantillons proviennent d'une collection dédiée (réglage de la section cart-drawer).
 *  - Ils sont ajoutés au panier via /cart/add.js avec des propriétés :
 *      _sample = 'true'                 → marqueur technique (repérage / exclusion des totaux)
 *      _Type   = 'Échantillon offert'   → propriété affichée
 *      _sample_threshold = <centimes>   → seuil revérifié côté serveur par la Shopify Function
 *    La mise à 0 € est appliquée par la Shopify Function `discount-echantillons`.
 *  - Vérification permanente : retrait auto si conditions non remplies.
 *  - Après chaque mutation on émet `cart:refresh` (event natif du thème) pour re-render le sidecart.
 *
 * Config injectée par le Liquid via `window.cartGiftSamplesConfig`.
 */
(function () {
  const cfg = window.cartGiftSamplesConfig;
  if (!cfg) return;

  const SAMPLE_HIDDEN_PROPERTY = '_sample';
  const HIDDEN_PROPERTY_VALUE = 'true';
  const DISPLAY_TYPE_PROPERTY = '_Type';

  const allowedSampleVariants = new Set(cfg.allowedSampleVariants || []);

  // Mémorise si l'accordéon était ouvert, pour le rouvrir après un re-render du sidecart
  let keepAccordionOpen = false;

  const config = {
    enableSamples: cfg.enableSamples === true,
    samplesMinimum: Number(cfg.samplesMinimum) || 0,
    samplesMaxQuantity: Number(cfg.samplesMaxQuantity) || 1,
    sampleTypeLabel: cfg.sampleTypeLabel || 'Échantillon offert',
  };

  /* ------------------------------------------------------------------ */
  /* Helpers panier                                                      */
  /* ------------------------------------------------------------------ */

  function calculateCartTotal() {
    return fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        let total = 0;
        cart.items.forEach((item) => {
          const properties = item.properties || {};
          // Ni les échantillons ni l'emballage cadeau ne financent le seuil.
          if (properties[SAMPLE_HIDDEN_PROPERTY] || properties._gift_wrapping) return;
          if (item.requires_shipping) total += item.final_line_price;
        });
        if (cart.cart_level_discount_applications) {
          cart.cart_level_discount_applications.forEach((discount) => {
            total -= discount.total_allocated_amount;
          });
        }
        return { total, cart };
      });
  }

  function removeFromCart(itemKey) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ updates: { [itemKey]: 0 } }),
    }).then((response) => response.json());
  }

  function findAllSampleItems(cart) {
    return cart.items.filter((item) => item.properties && item.properties[SAMPLE_HIDDEN_PROPERTY]);
  }

  function addSamplesToCart(variantIds) {
    const limited = variantIds.slice(0, config.samplesMaxQuantity);
    const items = limited.map((variantId) => ({
      id: Number(variantId),
      quantity: 1,
      properties: {
        [SAMPLE_HIDDEN_PROPERTY]: HIDDEN_PROPERTY_VALUE,
        _sample_threshold: config.samplesMinimum,
        _sample_max: config.samplesMaxQuantity,
        [DISPLAY_TYPE_PROPERTY]: config.sampleTypeLabel,
      },
    }));

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items }),
    }).then((response) => response.json());
  }

  function refreshCart() {
    // `cart:refresh` ne re-rend que le sidecart ; la page panier se recharge.
    if (window.themeVariables?.settings?.pageType === 'cart') {
      window.location.reload();
      return;
    }
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  // Retire les échantillons devenus non conformes (seuil, max, hors collection, feature off)
  function checkAndRemoveSamples() {
    return calculateCartTotal()
      .then(({ total, cart }) => {
        const sampleItems = findAllSampleItems(cart);
        if (sampleItems.length === 0) return false;

        const updates = [];
        // `total <= 0` est indispensable en plus du seuil : celui-ci vaut 0, donc
        // `total < 0` n'attrape jamais le panier vidé de ses produits payants.
        if (!config.enableSamples || total <= 0 || total < config.samplesMinimum) {
          sampleItems.forEach((item) => updates.push(removeFromCart(item.key)));
        } else if (sampleItems.length > config.samplesMaxQuantity) {
          sampleItems.slice(config.samplesMaxQuantity).forEach((item) => updates.push(removeFromCart(item.key)));
        } else {
          sampleItems.forEach((item) => {
            if (!allowedSampleVariants.has(item.variant_id)) updates.push(removeFromCart(item.key));
          });
        }

        if (updates.length > 0) {
          return Promise.all(updates).then(() => {
            refreshCart();
            return true;
          });
        }
        return false;
      })
      .catch((error) => {
        console.error('Erreur lors de la vérification des échantillons :', error);
        return false;
      });
  }

  /* ------------------------------------------------------------------ */
  /* Accordéon de sélection                                              */
  /* ------------------------------------------------------------------ */

  function updateValidateState(accordion) {
    const validate = accordion.querySelector('.samples-accordion__validate');
    if (!validate) return;
    const maxQuantity = parseInt(validate.getAttribute('data-max-quantity') || '1', 10);
    const checkboxes = accordion.querySelectorAll('.gift-drawer__product-checkbox');
    const checkedBoxes = accordion.querySelectorAll('.gift-drawer__product-checkbox:checked');
    const countSpan = accordion.querySelector('.samples-accordion__validate-count');
    const count = checkedBoxes.length;

    if (countSpan) countSpan.textContent = count > 0 ? ` (${count})` : '';
    // Le bouton reste actif même à 0 coché : valider une sélection vide retire les
    // échantillons déjà présents dans le panier.
    validate.disabled = false;

    checkboxes.forEach((checkbox) => {
      if (checkbox.checked) {
        checkbox.disabled = false;
      } else {
        checkbox.disabled = count >= maxQuantity;
      }
      checkbox.closest('.gift-sample-item')?.classList.toggle('is-disabled', checkbox.disabled && !checkbox.checked);
    });
  }

  function toggleAccordion(accordion, open) {
    const summary = accordion.querySelector('.samples-accordion__summary');
    const panel = accordion.querySelector('.samples-accordion__panel');
    if (!summary || !panel) return;
    const willOpen = open !== undefined ? open : panel.hasAttribute('hidden');
    if (willOpen) {
      panel.removeAttribute('hidden');
      summary.setAttribute('aria-expanded', 'true');
      accordion.classList.add('is-open');
      keepAccordionOpen = true;
    } else {
      panel.setAttribute('hidden', '');
      summary.setAttribute('aria-expanded', 'false');
      accordion.classList.remove('is-open');
      keepAccordionOpen = false;
    }
  }

  function validateSelection(accordion) {
    const validate = accordion.querySelector('.samples-accordion__validate');
    if (!validate || validate.disabled) return;

    const maxQuantity = parseInt(validate.getAttribute('data-max-quantity') || '1', 10);
    let checkedBoxes = Array.from(accordion.querySelectorAll('.gift-drawer__product-checkbox:checked'));
    if (checkedBoxes.length > maxQuantity) checkedBoxes = checkedBoxes.slice(0, maxQuantity);

    const variantIds = checkedBoxes.map((cb) => cb.getAttribute('data-variant-id'));
    validate.disabled = true;
    validate.classList.add('is-loading');

    calculateCartTotal()
      .then(({ total, cart }) => {
        // Seuil requis uniquement pour AJOUTER des échantillons.
        // Une validation à 0 coché sert à tout retirer : toujours autorisée.
        if (variantIds.length > 0 && (total <= 0 || total < config.samplesMinimum)) {
          validate.disabled = false;
          validate.classList.remove('is-loading');
          alert("Le montant minimum du panier n'est pas atteint pour bénéficier des échantillons.");
          return Promise.reject('min');
        }
        const existing = findAllSampleItems(cart);
        return Promise.all(existing.map((item) => removeFromCart(item.key)));
      })
      .then(() => {
        // Rien de coché → on a juste retiré les échantillons existants.
        if (variantIds.length === 0) return null;
        return addSamplesToCart(variantIds);
      })
      .then(() => refreshCart())
      .catch((error) => {
        if (error !== 'min') {
          console.error("Erreur lors de l'ajout des échantillons :", error);
          validate.disabled = false;
          validate.classList.remove('is-loading');
          alert("Une erreur est survenue lors de l'ajout au panier.");
        }
      });
  }

  /* ------------------------------------------------------------------ */
  /* Événements (délégation : survivent au re-render du sidecart)        */
  /* ------------------------------------------------------------------ */

  // Ouverture / fermeture de l'accordéon + validation.
  // Phase capture (true) : indispensable car le cart-drawer stoppe la propagation
  // des clics sur ses éléments internes en phase bubbling.
  document.addEventListener(
    'click',
    function (e) {
      const summary = e.target.closest('.samples-accordion__summary');
      if (summary) {
        e.preventDefault();
        e.stopPropagation();
        toggleAccordion(summary.closest('samples-accordion'));
        return;
      }

      const validate = e.target.closest('.samples-accordion__validate');
      if (validate) {
        e.preventDefault();
        e.stopPropagation();
        validateSelection(validate.closest('samples-accordion'));
      }
    },
    true
  );

  // Cases à cocher : compteur + limite max (capture aussi, par cohérence)
  document.addEventListener(
    'change',
    function (e) {
      const checkbox = e.target.closest('.samples-accordion .gift-drawer__product-checkbox');
      if (!checkbox) return;
      const accordion = checkbox.closest('samples-accordion');
      if (!accordion) return;

      if (checkbox.checked) {
        const validate = accordion.querySelector('.samples-accordion__validate');
        const maxQuantity = parseInt(validate?.getAttribute('data-max-quantity') || '1', 10);
        const checkedBoxes = accordion.querySelectorAll('.gift-drawer__product-checkbox:checked');
        if (checkedBoxes.length > maxQuantity) {
          checkbox.checked = false;
          updateValidateState(accordion);
          return;
        }
      }
      updateValidateState(accordion);
    },
    true
  );

  // Init de l'état des accordéons présents (après chaque re-render du sidecart)
  function initAccordions() {
    document.querySelectorAll('samples-accordion').forEach((accordion) => {
      if (keepAccordionOpen) toggleAccordion(accordion, true);
      updateValidateState(accordion);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAccordions();
    document.addEventListener('cart:change', checkAndRemoveSamples);
    document.addEventListener('cart-drawer:refreshed', initAccordions);
    checkAndRemoveSamples();
  });
})();
